import React, { useEffect, useState } from 'react';
import { Typography, Tag, Spin, Empty, Space, Divider, Collapse } from 'antd';
import { SoundOutlined } from '@ant-design/icons';
import api from '../api';

/**
 * Call context alongside an evaluation, for reading while scoring.
 *
 * Deliberately not the contact detail page shrunk down. The case page already
 * shows agent, date, campaign, skill, duration, media, disposition, direction
 * and end reason in its own block, so repeating them here would spend the
 * width on things already on screen. What it carries instead is the material
 * the case page has no version of: the AI summary, what the call was about,
 * and how it ended.
 *
 * Laid out one field per line because it lives in a quarter of the screen. A
 * two-column grid at this width wraps every value onto its own line anyway,
 * just less predictably.
 *
 * Sectioned and collapsed for the same reason: everything is tall at this
 * width, and a pane that buries the summary under six screens of tags is
 * worse than no pane.
 *
 * Not a Drawer. A Drawer renders a mask that swallows clicks and traps focus,
 * so the form behind it cannot be typed into -- which is the one thing this
 * has to allow. It is a fixed panel and the page pads itself out of the way.
 */

const LINK = 'https://na1.nice-incontact.com/player/#/cxone-player';

function Field({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

function TagList({ items, field }) {
  const vals = (items || []).map((r) => r[field]).filter(Boolean);
  if (vals.length === 0) return null;
  return (
    <Space size={[4, 4]} wrap>
      {vals.map((v, i) => <Tag key={`${v}-${i}`} style={{ margin: 0 }}>{v}</Tag>)}
    </Space>
  );
}

export default function CaseCallPane({ contactId, isAgent }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetched when the pane is first opened, not when the case loads. Collapsed
  // is the default, so most page views never pay for this at all.
  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    setError(null);
    api.get(`/api/calls/${contactId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Could not load call details.'))
      .finally(() => setLoading(false));
  }, [contactId]);

  if (loading) return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;
  if (error)   return <Empty description={error} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  if (!detail) return null;

  const { call_log, summary, reasons, outcomes, topics, flags, transcript, interaction_id, segment_id } = detail;
  const master = call_log?.MasterContactID ?? contactId;

  const open = (url) => window.open(url, '_blank', 'noopener,noreferrer');
  const aiCount = (topics?.length || 0) + (reasons?.length || 0) + (outcomes?.length || 0);

  return (
    <div style={{ fontSize: 13 }}>
      {/* Recordings first: the most likely reason to open this while scoring. */}
      <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 12 }}>
        {interaction_id && (
          <Typography.Link onClick={() => open(`${LINK}/interactions/${interaction_id}?acd-contacts=${master}`)}>
            <SoundOutlined /> Play Interaction
          </Typography.Link>
        )}
        {segment_id ? (
          <Typography.Link onClick={() => open(`${LINK}/segments/${segment_id}`)}>
            <SoundOutlined /> Play Segment
          </Typography.Link>
        ) : !isAgent && (
          <Typography.Link onClick={() => open(`${LINK}/acd-contacts/${contactId}`)}>
            <SoundOutlined /> ACD Recording Link
          </Typography.Link>
        )}
        {!interaction_id && !segment_id && isAgent && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>No recording found</Typography.Text>
        )}
      </Space>

      <Divider style={{ margin: '8px 0' }} />

      {/* Collapsed sections rather than one long column. At a quarter of the
          width everything is tall, and an open pane that buries the summary
          under six screens of tags is worse than no pane. Summary starts open
          because it is the reason to open this at all; the rest are a click
          when you want them. */}
      <Collapse
        size="small"
        ghost
        defaultActiveKey={['summary']}
        items={[
          {
            key: 'summary',
            label: 'Summary',
            children: summary?.Summary
              ? <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: 13 }}>{summary.Summary}</div>
              : <Typography.Text type="secondary">None</Typography.Text>,
          },
          {
            key: 'outcome',
            label: 'Outcome',
            children: (
              <>
                <Field label="Resolved">
                  {summary?.IsResolved != null
                    ? <Tag color={String(summary.IsResolved).toLowerCase().startsWith('y') ? 'green' : 'orange'} style={{ margin: 0 }}>
                        {String(summary.IsResolved)}
                      </Tag>
                    : null}
                </Field>
                <Field label="Stars">{summary?.Stars != null ? `${summary.Stars} / 5` : null}</Field>
                <Field label="Hold Time">{call_log?.hold_time != null ? `${call_log.hold_time}s` : null}</Field>
                <Field label="Location">
                  {[summary?.City, summary?.State, summary?.Zip].filter(Boolean).join(', ') || null}
                </Field>
              </>
            ),
          },
          {
            key: 'ai',
            // Counted in the label so the section says whether it is worth
            // opening without being opened.
            label: `AI Data${aiCount ? ` (${aiCount})` : ''}`,
            children: aiCount ? (
              <>
                <Field label="Topics"><TagList items={topics} field="Topic" /></Field>
                <Field label="Reasons"><TagList items={reasons} field="Reason" /></Field>
                <Field label="Outcomes"><TagList items={outcomes} field="Outcome" /></Field>
              </>
            ) : <Typography.Text type="secondary">None</Typography.Text>,
          },
          ...(transcript ? [{
            key: 'transcript',
            label: 'Transcript',
            children: (
              // Scrolls inside its own box rather than lengthening the pane,
              // so opening it does not push everything else out of reach.
              // Monospace keeps the [mm:ss] stamps in a column, which is what
              // makes it skimmable for the moment you are looking for.
              <div style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                lineHeight: 1.55,
                maxHeight: '45vh',
                overflowY: 'auto',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                padding: 8,
              }}>
                {transcript}
              </div>
            ),
          }] : []),
          ...(flags?.length ? [{
            key: 'flags',
            label: `Flags (${flags.length})`,
            children: (
              <Space direction="vertical" size={2}>
                {flags.map((f, i) => <Tag key={i} color="red" style={{ margin: 0 }}>{f.FlagType}</Tag>)}
              </Space>
            ),
          }] : []),
        ]}
      />
    </div>
  );
}
